import React, { useState, useEffect } from 'react';
import logoImg from '../assets/blast-logo.webp';
import tireSplashImg from '../assets/tire-splash.png';

export default function Splash({ onEnter }) {
  const [entered, setEntered] = useState(false);

  const handleClick = () => {
    if (entered) return;
    setEntered(true);
    setTimeout(() => {
      onEnter();
    }, 500);
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div
      onClick={handleClick}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        background: '#000',
        cursor: 'pointer',
        zIndex: 999,
      }}
    >
      <style>{`
        @keyframes screenShake {
          0% { transform: translate(0,0); }
          15% { transform: translate(-7px, 4px); }
          30% { transform: translate(6px, -5px); }
          45% { transform: translate(-5px, 3px); }
          60% { transform: translate(4px, -2px); }
          75% { transform: translate(-2px, 1px); }
          100% { transform: translate(0,0); }
        }
        @keyframes burstIn {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.1) rotate(-15deg); }
          50% { opacity: 1; }
          70% { transform: translate(-50%, -50%) scale(1.12) rotate(2deg); }
          100% { opacity: 0.9; transform: translate(-50%, -50%) scale(1) rotate(0deg); }
        }
        @keyframes flashPulse {
          0% { opacity: 0; }
          45% { opacity: 0.9; }
          100% { opacity: 0; }
        }
        @keyframes logoImpact {
          0% { opacity: 0; transform: scale(1.8); filter: blur(6px); }
          55% { opacity: 1; transform: scale(0.94); filter: blur(0px); }
          75% { transform: scale(1.05); }
          100% { opacity: 1; transform: scale(1); filter: blur(0px); }
        }
        @keyframes hintAppear { to { opacity: 0.9; } }
        @keyframes hintPulse {
          0%, 100% { opacity: 0.9; }
          50% { opacity: 0.25; }
        }
        @keyframes stripSlide {
          to { opacity: 1; transform: skewY(-2deg) translateY(0); }
        }
        @keyframes splashFadeOut { to { opacity: 0; } }

        .blast-splash-inner {
          position: relative;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background:
            radial-gradient(ellipse at 30% 20%, rgba(45,60,75,0.4) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 80%, rgba(30,35,42,0.5) 0%, transparent 50%),
            linear-gradient(135deg, #14171c 0%, #0a0b0d 50%, #16191e 100%);
          animation: screenShake 0.4s ease-out;
          animation-delay: 0.55s;
        }
        .blast-splash-inner.fade-out {
          animation: splashFadeOut 0.5s ease forwards;
        }
        .blast-tire-splash {
          position: absolute;
          width: min(140vw, 1400px);
          height: min(140vw, 1400px);
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%) scale(0.15) rotate(-8deg);
          opacity: 0;
          filter: brightness(0.85);
          animation: burstIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          animation-delay: 0.15s;
        }
        .blast-impact-flash {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at center, rgba(240,237,228,0.95) 0%, rgba(240,237,228,0) 60%);
          opacity: 0;
          pointer-events: none;
          animation: flashPulse 0.3s ease forwards;
          animation-delay: 0.5s;
        }
        .blast-logo-wrap {
          position: relative;
          z-index: 5;
          display: flex;
          flex-direction: column;
          align-items: center;
          opacity: 0;
          transform: scale(1.8);
          filter: blur(6px);
          animation: logoImpact 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          animation-delay: 0.6s;
        }
        .blast-logo-img {
          width: clamp(260px, 42vw, 480px);
          height: auto;
          filter: drop-shadow(0 0 50px rgba(43,105,150,0.6)) drop-shadow(0 4px 20px rgba(0,0,0,0.8));
        }
        .blast-tagline {
          margin-top: -6px;
          font-family: 'Segoe UI', sans-serif;
          font-weight: 700;
          font-size: clamp(11px, 2vw, 15px);
          letter-spacing: 8px;
          color: #8B92A0;
          text-transform: uppercase;
          opacity: 0;
          animation: hintAppear 0.5s ease forwards;
          animation-delay: 1.0s;
        }
        .blast-tap-hint {
          position: absolute;
          bottom: 60px;
          left: 50%;
          transform: translateX(-50%);
          font-family: 'Segoe UI', sans-serif;
          font-size: 13px;
          letter-spacing: 3px;
          color: #F0EDE4;
          text-transform: uppercase;
          opacity: 0;
          animation: hintAppear 0.6s ease forwards, hintPulse 1.6s ease-in-out infinite;
          animation-delay: 1.4s, 2.0s;
        }
        .blast-checker-strip {
          position: absolute;
          bottom: 0;
          left: -10%;
          width: 120%;
          height: 26px;
          background-image: repeating-linear-gradient(
            90deg,
            #F0EDE4 0px, #F0EDE4 20px,
            #12151A 20px, #12151A 40px
          );
          transform: skewY(-2deg) translateY(60px);
          opacity: 0;
          animation: stripSlide 0.5s cubic-bezier(0.65,0,0.35,1) forwards;
          animation-delay: 0.75s;
          z-index: 6;
        }
      `}</style>

      <div className={`blast-splash-inner${entered ? ' fade-out' : ''}`}>
        <img className="blast-tire-splash" src={tireSplashImg} alt="" />
        <div className="blast-impact-flash" />
        <div className="blast-checker-strip" />
        <div className="blast-logo-wrap">
          <img className="blast-logo-img" src={logoImg} alt="Blast Motor Club" />
          <div className="blast-tagline">Motor Club</div>
        </div>
        <div className="blast-tap-hint">Ketuk dimana saja untuk masuk</div>
      </div>
    </div>
  );
}
